import { useState } from 'react';
import type { ChangeEvent, FormEvent, ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { loginAuthUser, bootstrapAuthAdmin } from '@/lib/api';
import { clearSessionExpired, wasSessionExpired } from '@/lib/session-expiry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface LoginScreenProps {
  /** When true, no operator account exists yet — render the first-run bootstrap form. */
  bootstrapRequired: boolean;
}

/**
 * Standalone sign-in / first-run bootstrap screen.
 *
 * Maestro is normally entered via the AgentMesh out-of-band session handoff, so
 * the SPA historically had no password login UI. For a standalone instance that
 * left the operator unable to mint a session. This screen calls the existing
 * `/api/auth/login` (or `/api/auth/bootstrap` on first run) and, on success,
 * invalidates the shared `['authSession']` query so `AuthGate` re-renders the app
 * authenticated — no page reload required.
 */
export function LoginScreen({ bootstrapRequired }: LoginScreenProps): ReactElement {
  const queryClient = useQueryClient();
  // Captured once at mount, not read per render: the operator should keep seeing why
  // they were signed out while they type, and the flag is cleared on success.
  const [expired] = useState(wasSessionExpired);
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const email = id.trim();
      if (bootstrapRequired) {
        return bootstrapAuthAdmin({
          id: email,
          name: name.trim(),
          orgName: orgName.trim() || undefined,
          password,
        });
      }
      return loginAuthUser({ id: email, password });
    },
    onSuccess: async () => {
      clearSessionExpired();
      await queryClient.invalidateQueries({ queryKey: ['authSession'] });
    },
  });

  const submitting = mutation.isPending;
  const submitError = mutation.error as (Error & { status?: number }) | null;
  const errorMessage =
    submitError == null
      ? null
      : submitError.status === 403
        ? 'Invalid email or password.'
        : (submitError.message ?? 'Sign in failed. Please try again.');

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (submitting) return;
    mutation.mutate();
  }

  const onIdChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setId(e.target.value);
  };
  const onPasswordChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPassword(e.target.value);
  };
  const onNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value);
  };
  const onOrgNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setOrgName(e.target.value);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface/60 p-8 shadow-xl">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-text-primary">Maestro-Prime</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {bootstrapRequired ? 'Create the operator account' : 'Sign in to continue'}
          </p>
          {expired && !bootstrapRequired && (
            <p className="mt-3 rounded-md border border-border bg-surface-elevated px-3 py-2 text-xs text-text-secondary">
              Your session expired, so Maestro signed you out. Sign in to pick up where you left
              off.
            </p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="auth-id" className="text-sm font-medium text-text-secondary">
              Email
            </label>
            <Input
              id="auth-id"
              type="email"
              autoComplete="username"
              value={id}
              onChange={onIdChange}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>

          {bootstrapRequired && (
            <>
              <div className="space-y-1.5">
                <label htmlFor="auth-name" className="text-sm font-medium text-text-secondary">
                  Name
                </label>
                <Input
                  id="auth-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={onNameChange}
                  placeholder="Aaron"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="auth-org" className="text-sm font-medium text-text-secondary">
                  Organization
                </label>
                <Input
                  id="auth-org"
                  type="text"
                  autoComplete="organization"
                  value={orgName}
                  onChange={onOrgNameChange}
                  placeholder="Default Organization"
                />
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <label htmlFor="auth-password" className="text-sm font-medium text-text-secondary">
              Password
            </label>
            <Input
              id="auth-password"
              type="password"
              autoComplete={bootstrapRequired ? 'new-password' : 'current-password'}
              value={password}
              onChange={onPasswordChange}
              required
            />
          </div>

          {errorMessage != null && (
            <p role="alert" className="text-sm text-error">
              {errorMessage}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting
              ? bootstrapRequired
                ? 'Creating account…'
                : 'Signing in…'
              : bootstrapRequired
                ? 'Create account'
                : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
}
